import React, { FunctionComponent, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useIntl } from 'react-intl';
import axios from 'axios';
import parse from 'html-react-parser';
import {
  API,
  availableDesksRoute,
  buildDataServerAuthorizationHeaders,
  reservationRoute,
  smartBuildingRoute,
} from '../../constants/ApiRoutes';
import { useStoreState } from '../../store/hooks';
import ServiceCard from './ServiceCard';
import { HomePageContainer, StCurrentSite } from './SiteServices.style';

import styles from './SiteServices.module.scss';

// L'ordre d'affichage des features de la home
export const ORDERED_FEATURES: { [key: string]: number } = {
  bureauxDispo: 1,
  activateBooking: 2,
  domotique: 4,
  laVallee: 5,
};

interface SiteInfoType {
  messagePubHaut: string;
  messagePubBas: string;
  afficheImg: boolean;
}

const SiteServices: FunctionComponent = () => {
  const intl = useIntl();
  const altImgNameDomotique = intl.formatMessage({
    id: 'servicesImgAltName.domotique',
  });
  const altImgNameBureauxDispo = intl.formatMessage({
    id: 'servicesImgAltName.bureauxDispo',
  });
  const altImgNameReservation = intl.formatMessage({
    id: 'servicesImgAltName.reservation',
  });

  const site = useStoreState((state) => state.sites);

  const [cookies] = useCookies<CookiesKeys>(['selected_country']);
  const currentTranslation = useStoreState((state) => state.intl.locale);
  const [siteInfo, setSiteInfo] = useState<SiteInfoType>();
  const buildingFolderId = cookies.selected_building ? cookies.selected_building.folderId : '';

  useEffect(() => {
    async function getBuildingData() {
      try {
        const response = await axios(
          `${API.SITES.GET_SITES}/${buildingFolderId}/page-aide?lang=${currentTranslation}`,
          {
            headers: buildDataServerAuthorizationHeaders(),
          }
        );

        if (response.data.status.code === '0') {
          setSiteInfo(response.data.result);
        }
      } catch (error) {
        alert(`Fetched site help info ERROR: ${error}`);
      }
    }

    void getBuildingData();
  }, [buildingFolderId, currentTranslation]);

  const getServiceInfo = (service: string) => {
    switch (service) {
      case 'domotique':
        return {
          logo: 'smartBuildingServiceLogo',
          imgAltName: `${altImgNameDomotique}`,
          pushPath: smartBuildingRoute,
        };
      case 'bureauxDispo':
        return {
          logo: 'desksServiceLogo',
          imgAltName: `${altImgNameBureauxDispo}`,
          pushPath: availableDesksRoute,
        };
      case 'activateBooking':
        return {
          logo: 'reservationLogo',
          imgAltName: `${altImgNameReservation}`,
          pushPath: reservationRoute,
        };
      default:
        return undefined;
    }
  };

  return (
    <HomePageContainer>
      <StCurrentSite>{site.selectedSite.toUpperCase()}</StCurrentSite>
      <div className={`${styles.marginFooter} container-fluid row p-0`}>
        {!!site && (
          <div className="col">
            <div className="row row-cols-2">
              {Object.keys(site.siteServices)
                .sort((a, b) => {
                  return ORDERED_FEATURES[a] - ORDERED_FEATURES[b];
                })
                .map((service) => {
                  const serviceInfo = getServiceInfo(service);

                  if (
                    (service === 'domotique' && site.siteServices.domotique) ||
                    (service === 'bureauxDispo' && site.siteServices.bureauxDispo) ||
                    (service === 'activateBooking' && site.siteServices.activateBooking)
                  ) {
                    if (!serviceInfo) {
                      return null;
                    }

                    return (
                      <div className="col d-flex" key={service}>
                        <ServiceCard
                          key={service}
                          title={service}
                          logo={serviceInfo.logo}
                          imgAltName={serviceInfo.imgAltName}
                          pushPath={serviceInfo.pushPath}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
            </div>
            <div className="row row-cols-1 m-auto">
              {siteInfo && (
                <div className="col p-4 container-fluid overflow-auto" style={{ maxHeight: '300px' }}>
                  {siteInfo && parse(siteInfo.messagePubHaut)}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="text-center col p-0 ms-3">
          <img
            src={`${import.meta.env.VITE_APP_SERVER_DATA}${site.siteServices.urlImage}`}
            alt=""
            className={styles.imgSite}
          />
        </div>
      </div>
    </HomePageContainer>
  );
};

export default SiteServices;
